import {Icon} from './Icon'
import {cva, type VariantProps} from 'class-variance-authority'
import type {InputHTMLAttributes} from 'react'
import {forwardRef} from 'react'

const checkboxWrapperVariants = cva('focus-group flex w-fit items-center gap-8', {
  variants: {
    disabled: {
      true: 'cursor-default',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

const checkboxIconVariants = cva(
  [
    'block size-16 transition-colors',
    'rounded-md border-1 border-border-button-secondary',
    'peer-checked:border-button-selected peer-checked:bg-button-selected peer-checked:[&>*]:opacity-100',
    'peer-disabled:cursor-default peer-disabled:opacity-50',
  ],
  {
    variants: {
      disabled: {
        true: 'border-border-button-secondary-disabled',
        false:
          'peer-hover:border-border-button-secondary-hover peer-active:border-border-button-secondary-pressed',
      },
    },
    defaultVariants: {
      disabled: false,
    },
  },
)

const labelVariants = cva('select-none', {
  variants: {
    size: {
      s: 'text-sm leading-sm',
      l: 'text-base leading-base',
    },
    disabled: {
      true: 'text-text-disabled',
      false: 'text-text',
    },
  },
  defaultVariants: {
    size: 's',
    disabled: false,
  },
})

/**
 * The visual box-and-checkmark icon from `Checkbox`, usable without a form
 * input — e.g. inside a `<Link>` for navigation-based toggles.
 *
 * Uses the same `checkboxIconVariants` as `Checkbox` so style updates apply
 * to both.
 *
 * - `isActive` omitted → peer mode: CSS peer classes respond to a sibling
 *   `<input className="peer">`. Used by `Checkbox` internally.
 * - `isActive` boolean → explicit mode: conditional classes control state.
 *   Used for Link-based toggles that have no peer input.
 */
interface CheckboxIndicatorProps {
  isActive?: boolean
  disabled?: boolean
  label?: string
  size?: VariantProps<typeof labelVariants>['size']
}

export function CheckboxIndicator({isActive, disabled, label, size}: CheckboxIndicatorProps) {
  return (
    <>
      <span
        className={checkboxIconVariants({
          disabled,
          className:
            isActive === true
              ? 'border-button-selected bg-button-selected *:opacity-100'
              : undefined,
        })}
      >
        <Icon
          name="check"
          className="ease-out size-full opacity-0 transition-opacity"
          aria-hidden="true"
          color="inverse"
        />
      </span>
      {label && <span className={labelVariants({size, disabled})}>{label}</span>}
    </>
  )
}

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>,
    VariantProps<typeof labelVariants> {
  label?: string
  disabled?: boolean
  expandClickArea?: boolean
}

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=6032-5329&m=dev
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({className, label, size, disabled, expandClickArea, ...props}, ref) => {
    return (
      <label className={checkboxWrapperVariants({disabled, className})}>
        <input {...props} type="checkbox" ref={ref} disabled={disabled} className="peer sr-only" />
        <CheckboxIndicator disabled={disabled} label={label} size={size} />
        {/* set expandClickArea to true, used on bigger component like Newsletter cards */}
        {/* must set "relative" class on the parent card which contains this checkbox */}
        {expandClickArea && <span className="inset-0 absolute" />}
      </label>
    )
  },
)
Checkbox.displayName = 'Checkbox'
